import { ENVELOPE_STATUSES } from '@platform/shared-types';
import type { EnvelopeStatus } from '@platform/shared-types';
import type { TagTone } from '@documents-ui';
import type { VersionSummary } from '../../services/documentsApi';

/**
 * Pure helpers for the Legal pages. They live outside the components because
 * this app has no DOM test renderer — logic left inside a .tsx is untestable
 * here (same reason services/dealPricing.ts exists).
 *
 * TWO RULES ARE ENCODED HERE AND NOWHERE ELSE:
 *
 *  1. EFFECTIVE DATES RENDER IN UTC. `effectiveAt` is UTC midnight of a legal
 *     calendar day, not an instant in the reader's day. Formatting it
 *     browser-local shows 28 February to a US reader for a document that takes
 *     effect on 1 March. This is a deliberate, documented exception to the
 *     house "displays are browser-local" rule.
 *
 *  2. STATUS IS DERIVED, NEVER STORED. `documentHeadState` reads only the head
 *     version's `status` plus the server-computed `isActive`/`isScheduled`/
 *     `isSuperseded`. There is no stored "superseded" status in this system and
 *     nothing below may invent one.
 */

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** '2026-03-01T00:00:00.000Z' → '01 Mar 2026'. UTC. Always. */
export function formatUtcDay(iso?: string | null): string {
  if (!iso) return '—';
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return String(iso);
  const d = new Date(t);
  return `${String(d.getUTCDate()).padStart(2, '0')} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

/** Audit-style stamp: '2026-08-22 09:14 UTC'. Also UTC, for the same reason. */
export function formatUtcStamp(iso?: string | null): string {
  if (!iso) return '—';
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return String(iso);
  const d = new Date(t);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getUTCFullYear()}-${p(d.getUTCMonth() + 1)}-${p(d.getUTCDate())} `
    + `${p(d.getUTCHours())}:${p(d.getUTCMinutes())} UTC`;
}

/** Permanent public URL segment. Matches the service's own regex. */
export const SLUG_PATTERN = /^[a-z0-9]+(-[a-z0-9]+)*$/;

export const isValidSlug = (s: string): boolean => SLUG_PATTERN.test(s);

/** Best-effort suggestion only — the user can always overwrite it. */
export function slugify(title: string): string {
  return title
    .toLowerCase()
    .replace(/['’]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/** Highest versionNo. The chain is linear and append-only, so this is the head. */
export function headVersion(versions: readonly VersionSummary[]): VersionSummary | null {
  if (!versions.length) return null;
  return versions.reduce((a, b) => (b.versionNo > a.versionNo ? b : a));
}

/** Statuses that still allow work: the head is being written, reviewed or held. */
const OPEN_STATUSES = ['draft', 'in_review', 'approved'] as const;

/**
 * The version currently in flight, if any. At most one can exist — the service
 * refuses to open a second draft while one of these is outstanding.
 */
export function openVersion(versions: readonly VersionSummary[]): VersionSummary | null {
  const head = headVersion(versions);
  if (!head) return null;
  return (OPEN_STATUSES as readonly string[]).includes(head.status) ? head : null;
}

/**
 * The version whose publication would force re-acceptance — i.e. one that has
 * left the author's hands. A draft is not counted: nothing is pending on a
 * document nobody has submitted yet. Matches the mockup's Pending column,
 * which is populated for "in review" and "approved" and blank for "draft".
 */
export function pendingVersion(versions: readonly VersionSummary[]): VersionSummary | null {
  const open = openVersion(versions);
  if (!open) return null;
  return open.status === 'in_review' || open.status === 'approved' ? open : null;
}

export interface HeadState {
  tone: TagTone;
  label: string;
}

/**
 * The Status cell. Derived from the head version and the server's derived
 * booleans — there is no stored status to read and none is invented here.
 */
export function documentHeadState(
  documentStatus: 'active' | 'retired',
  versions: readonly VersionSummary[],
): HeadState {
  if (documentStatus === 'retired') return { tone: 'retired', label: 'retired' };

  const head = headVersion(versions);
  if (!head) return { tone: 'draft', label: 'no versions' };

  switch (head.status) {
    case 'draft': return { tone: 'draft', label: `v${head.versionNo} draft` };
    case 'in_review': return { tone: 'review', label: `v${head.versionNo} in review` };
    case 'approved': return { tone: 'approved', label: `v${head.versionNo} approved` };
    default: break;
  }
  // published
  if (head.isScheduled) return { tone: 'scheduled', label: `v${head.versionNo} scheduled` };
  if (head.isActive) return { tone: 'published', label: 'published' };
  // A published head that is neither active nor scheduled has been overtaken by
  // an earlier-numbered version with a later effective date. Derived, not stored.
  return { tone: 'draft', label: `v${head.versionNo} superseded` };
}

/** The version that governs right now, or null if nothing has taken effect. */
export function activeVersionRow(
  versions: readonly VersionSummary[], activeVersionNo: number | null,
): VersionSummary | null {
  if (activeVersionNo === null) return null;
  return versions.find((v) => v.versionNo === activeVersionNo) ?? null;
}

/** The published version immediately before `versionNo`, for the diff baseline. */
export function previousVersionNo(
  versions: readonly VersionSummary[], versionNo: number,
): number | null {
  const lower = versions.filter((v) => v.versionNo < versionNo).map((v) => v.versionNo);
  return lower.length ? Math.max(...lower) : null;
}

/** Human label for a version status, used in headers and the chain caption. */
export const VERSION_STATUS_LABEL: Record<string, string> = {
  draft: 'draft',
  in_review: 'in review',
  approved: 'approved',
  published: 'published',
};

/**
 * The highest-numbered version with status `published`, or null.
 *
 * This is the question envelope creation asks — "is there a published version
 * an envelope can NAME?" — and it is deliberately NOT the same question as
 * `activeVersionNo`. A version published with a future effective date is
 * published but not yet active; it can still be named, so it counts here.
 * Reading `activeVersionNo` instead would call a scheduled template unsendable.
 */
export function latestPublishedVersion(
  versions: readonly VersionSummary[],
): VersionSummary | null {
  const published = versions.filter((v) => v.status === 'published');
  if (!published.length) return null;
  return published.reduce((a, b) => (b.versionNo > a.versionNo ? b : a));
}

/* ── system documents ───────────────────────────────────────────────────── */

/**
 * Where a SYSTEM document stands, for the System documents panel.
 *
 *  - `empty`  — never written: one v1 draft with no blocks, nothing in force.
 *  - `live`   — a version is in force (a newer draft/review may also exist).
 *  - `review` — nothing in force, head is in review or approved.
 *  - `draft`  — nothing in force, head is a draft with content.
 *
 * Derived from the chain the list endpoint projects; never stored.
 */
export type SystemDocPhase = 'empty' | 'live' | 'review' | 'draft';

export function systemDocPhase(
  versions: readonly (Pick<VersionSummary, 'versionNo' | 'status' | 'blockCount'> & { hasContent?: boolean })[],
  activeVersionNo: number | null | undefined,
): SystemDocPhase {
  if (activeVersionNo !== null && activeVersionNo !== undefined) return 'live';
  const head = headVersion(versions as VersionSummary[]);
  if (!head) return 'empty';
  if (head.status === 'in_review' || head.status === 'approved') return 'review';
  const hasPublished = versions.some((v) => v.status === 'published');
  if (versions.length === 1 && head.status === 'draft' && !(head.blockCount > 0) && !head.hasContent && !hasPublished) return 'empty';
  return 'draft';
}

/* ── envelopes ──────────────────────────────────────────────────────────── */

/**
 * A POLICY IS ACCEPTED; AN AGREEMENT IS SIGNED VIA AN ENVELOPE. Everything
 * below serves the second half of that sentence and has no bearing on the
 * acceptance ledger.
 */

/** Human spelling. Only `partially_signed` differs from its stored value. */
export const ENVELOPE_STATUS_LABEL: Record<EnvelopeStatus, string> = {
  draft: 'draft',
  sent: 'sent',
  partially_signed: 'partially signed',
  executed: 'executed',
  declined: 'declined',
  voided: 'voided',
  expired: 'expired',
};

/**
 * Tone per status, reusing the document tones so one colour means one thing
 * across the section: emerald = finished and good, amber = somebody is being
 * waited on, blue = in progress, red = over and not good, grey = not started.
 */
export const ENVELOPE_STATUS_TONE: Record<EnvelopeStatus, TagTone> = {
  draft: 'draft',
  sent: 'review',
  partially_signed: 'approved',
  executed: 'published',
  declined: 'material',
  voided: 'retired',
  expired: 'retired',
};

/** Terminal statuses — the envelope's story is over and it is re-issued, never revived. */
const CLOSED_STATUSES: readonly EnvelopeStatus[] = ['declined', 'voided', 'expired'];
/** Somebody is expected to act. A draft is NOT awaiting anyone — it was never sent. */
const AWAITING_STATUSES: readonly EnvelopeStatus[] = ['sent', 'partially_signed'];

export interface EnvelopeTally {
  total: number;
  /** `sent` + `partially_signed`. */
  awaiting: number;
  executed: number;
  /** Created but never sent, so nobody is waiting on it. */
  draft: number;
  /** `declined` + `voided` + `expired`. */
  closed: number;
  byStatus: Record<EnvelopeStatus, number>;
}

const zeroByStatus = (): Record<EnvelopeStatus, number> => {
  const out = {} as Record<EnvelopeStatus, number>;
  ENVELOPE_STATUSES.forEach((s) => { out[s] = 0; });
  return out;
};

export function tallyEnvelopes(rows: readonly { status: EnvelopeStatus }[]): EnvelopeTally {
  const byStatus = zeroByStatus();
  rows.forEach((r) => {
    // An unrecognised status is COUNTED IN THE TOTAL but not attributed to a
    // bucket it does not belong to. Adding it to `byStatus` would create a key
    // no column reads; folding it into `closed` would report an envelope as
    // finished on the strength of a value this build does not understand.
    if (byStatus[r.status] !== undefined) byStatus[r.status] += 1;
  });
  return {
    total: rows.length,
    awaiting: AWAITING_STATUSES.reduce((n, s) => n + byStatus[s], 0),
    executed: byStatus.executed,
    draft: byStatus.draft,
    closed: CLOSED_STATUSES.reduce((n, s) => n + byStatus[s], 0),
    byStatus,
  };
}

/**
 * Whether a template's envelope counts are known, and if not, why not.
 *
 * The three cases are kept apart on purpose. `loading` and `error` are both
 * "no number", and both must render as a dash — but they are not the same
 * thing to a reader deciding whether to hit Refresh or to worry.
 */
export type EnvelopeTallyState =
  | { kind: 'loading' }
  | { kind: 'error'; error: string }
  | { kind: 'ready'; tally: EnvelopeTally };

export type EnvelopeCountField = 'total' | 'awaiting' | 'executed' | 'draft' | 'closed';

/**
 * THE ONE RULE THIS SECTION EXISTS FOR.
 *
 * `0` is now a real answer and must be shown as `0` — it means the template has
 * no envelopes in that state, which is a fact somebody may act on. A fetch that
 * failed or has not landed is NOT zero and must never render as one; it renders
 * as a dash, because "we do not know" and "none" are different facts and a
 * column of zeroes that actually meant "we could not ask" is the specific lie
 * this page previously refused to tell by omitting the columns entirely.
 *
 * Both directions matter: hiding a legitimate 0 behind a dash is the same
 * failure with the sign flipped.
 */
export function envelopeCountText(
  state: EnvelopeTallyState | undefined, field: EnvelopeCountField,
): string {
  if (!state || state.kind !== 'ready') return '—';
  return String(state.tally[field]);
}

/** Why there is a dash, for the cell's `title`. Empty when there is a number. */
export function envelopeCountTitle(state: EnvelopeTallyState | undefined): string {
  if (!state) return 'Envelope counts have not been requested for this template yet.';
  if (state.kind === 'loading') return 'Counting envelopes…';
  if (state.kind === 'error') {
    return `Envelope counts could not be read (${state.error}). This is not a zero — `
      + 'nothing is known about this template\u2019s envelopes.';
  }
  return '';
}
